import React from 'react';
import { LinkIcon } from '../../../components/icons';
import { CplOrder } from '../../../api/client/clientCplService';
import { OrderData } from './types';
import { STATUS_LABELS, getStatusLabels } from './constants';
import { getPlatformIcon, transformPlatform, getPlatformIconForRender } from '../../../utils';

export const transformStatus = (status: string): string => {
  switch (status) {
    case 'in_progress':
      return 'in_progress';
    case 'waiting':
      return 'pending';
    case 'completed':
      return 'completed';
    case 'canceled':
      return 'canceled';
    case 'moderation':
    case 'in_moderation':
      return 'moderation';
    default:
      return status;
  }
};

export const transformCplOrderToOrderData = (cplOrder: CplOrder): OrderData => {
  // Вычисляем статистику транзакций
  const transactionStats = cplOrder.transactions_statistic || {};
  const totalTransactionsRaw = transactionStats?.total;
  const transactionsTotal = Number(totalTransactionsRaw ?? 0) || 0;

  const transactionsBreakdown = Object.entries(transactionStats).reduce(
    (acc, [key, value]) => {
      if (key === 'total') return acc;
      const numericValue = typeof value === 'number' ? value : Number(value ?? 0);
      acc[key] = Number.isFinite(numericValue) ? numericValue : 0;
      return acc;
    },
    {} as Record<string, number>
  );
  const breakdownValues = Object.values(transactionsBreakdown);
  const purchasesCountFromBreakdown = breakdownValues.length
    ? breakdownValues.reduce((acc, value) => acc + value, 0)
    : 0;

  const totalSubscribers = Number(cplOrder.subs.count) || 0;
  const verifiedUsers = Number(cplOrder.subscribed_count) || 0;
  const transitionsCount = Number(cplOrder?.model?.transitions_count) || 0;
  const purchasesCount = cplOrder.transactions_statistic.count;

  const cost = Number(cplOrder.total_price) || 0;
  const revenue = Number(transactionStats?.total ?? 0) || 0;
  // ARPU (план) = планируемая стоимость за подписчика
  const arpuPlan = totalSubscribers > 0 ? parseFloat((cost / totalSubscribers).toFixed(2)) : 0;
  // ARPU($) (факт) = реальный доход на подписчика
  const arpuActual = verifiedUsers > 0 ? parseFloat((revenue / verifiedUsers).toFixed(2)) : 0;
  const romi = cost > 0 ? parseFloat((((revenue - cost) / cost) * 100).toFixed(2)) : 0;

  return {
    ...cplOrder,
    id: cplOrder.id,
    internalId: cplOrder.id.slice(-4),
    account: {
      id: cplOrder.model.id,
      of_id: cplOrder.model.of_user_id,
      name: cplOrder.model.of_user_name,
      username: cplOrder.model.of_user_name,
      avatar: cplOrder.model.avatar || undefined,
      platform: transformPlatform(cplOrder.offer.source),
      platformIcon: getPlatformIcon(cplOrder.offer.source),
      overall_popularity_percent: cplOrder.model.overall_popularity_percent || 0,
    },
    offer: {
      type: cplOrder.offer.type,
      id: cplOrder.offer.id,
      name: cplOrder.offer.partner.name,
      username: cplOrder.model.of_user_name,
      avatar: cplOrder.offer.partner.avatar || undefined,
      platform: transformPlatform(cplOrder.offer.source),
      subscribers: cplOrder.subs.count,
      pricePerSubscriber: parseFloat(cplOrder.subs.price) || 0,
      description:
        cplOrder.offer.description ||
        'Quality & new traffic from OF pages Only fans buyers / no bots Freeloaders are blocked daily',
    },
    campaign: {
      id: cplOrder.id.slice(-4),
      type: cplOrder.model.type === 'free' ? 'free' : 'paid',
      commission: `${cplOrder.commition}%`,
      status: 'access_active',
      link: cplOrder.model.link,
      linkSuffix: cplOrder.model.link
        ? cplOrder.model.link.split('/').filter(Boolean).slice(-1)[0] || ''
        : '',
      views: cplOrder.model.track_link_id || 0,
      transitions_count: transitionsCount,
      transitions_percentage:
        transitionsCount > 0 ? `${((verifiedUsers / transitionsCount) * 100).toFixed(2)}%` : '0%',
      conversionRate:
        totalSubscribers > 0
          ? `${((totalSubscribers / (cplOrder.model.track_link_id || 1)) * 100).toFixed(2)}%`
          : '0%',
      statistics: {
        totalUsers: totalSubscribers,
        verifiedUsers: verifiedUsers,
        messages: cplOrder?.subs?.with_gte_3_chat_messages_count || 0,
        transactions: {
          total: transactionsTotal,
          count: purchasesCount,
          breakdown: transactionsBreakdown,
        },
        cost: cost,
        arpu: arpuPlan,
        arpuUsd: arpuActual,
        romi: romi,
      },
    },
    status: transformStatus(cplOrder.status),
    startDate: cplOrder.created_at,
    createdAt: cplOrder.created_at,
  };
};

export const getStatusLabel = (status: string, t?: (key: string) => string): string => {
  if (t) {
    const statusLabels = getStatusLabels(t);
    return statusLabels[status as keyof typeof statusLabels] || status;
  }
  return STATUS_LABELS[status as keyof typeof STATUS_LABELS] || status;
};

export const getPlatformIconForRenderCustom = (platform: string): React.ReactNode => {
  switch (platform) {
    case 'OnlyFans':
      return React.createElement(LinkIcon, { className: 'w-3.5 h-3.5' });
    default:
      return getPlatformIconForRender(platform, 'w-3.5 h-3.5');
  }
};
